import { useEffect, useMemo, useRef, useState } from 'react';

type UseVideoOptions = {
  manageVisibility?: boolean;
  intersectionThreshold?: number;
  shouldPlay?: boolean;
  deferUntilVisible?: boolean;
};

const normalizeUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return url;
};

export function useVideo(
  url: string | undefined,
  {
    manageVisibility = false,
    intersectionThreshold = 0.25,
    shouldPlay = true,
    deferUntilVisible = false
  }: UseVideoOptions = {}
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const normalized = useMemo(() => normalizeUrl(url), [url]);
  const [hasAttachedSource, setHasAttachedSource] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !normalized) return;

    if (deferUntilVisible && manageVisibility) {
      return;
    }

    el.src = normalized;
    setHasAttachedSource(true);
  }, [normalized, manageVisibility, deferUntilVisible]);

  useEffect(() => {
    if (!manageVisibility) return;
    const el = videoRef.current;
    if (!el) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) {
      el.pause();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;

          if (deferUntilVisible && !hasAttachedSource && e.isIntersecting) {
            v.src = normalized;
            setHasAttachedSource(true);
          }

          if (e.isIntersecting && shouldPlay) {
            const result = v.play();
            (result as unknown as Promise<void> | undefined)?.catch?.(
              () => undefined
            );
          } else {
            v.pause();
          }
        });
      },
      { threshold: intersectionThreshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [
    manageVisibility,
    intersectionThreshold,
    shouldPlay,
    deferUntilVisible,
    hasAttachedSource,
    normalized
  ]);

  const effectiveSourceUrl = normalized;

  return { videoRef, sourceUrl: effectiveSourceUrl } as const;
}
